// ─────────────────────────────────────────────────────────────────
// LessonProgressBar — shared five-step progress bar for the Study Console
// and the full-screen checkpoint (section 8 / 9 of the redesign).
//
// Nodes: Teach → Practice → Repair → Checkpoint → Mastery Cleared.
// Purely presentational — driven by the engine's sessionPhase. Node states:
//   completed → emerald filled + checkmark
//   active    → amber filled + step number
//   locked    → slate outline + padlock
// Connector i→i+1 is emerald when node i is completed, slate otherwise.
// ─────────────────────────────────────────────────────────────────

const NODES = ['Teach', 'Practice', 'Repair', 'Checkpoint', 'Mastery Cleared'];

// Per-phase node states. The happy path (Checkpoint) leaves Repair locked —
// repair is only reached when a checkpoint fails (REMEDIATION).
const PHASE_NODE_STATE = {
  PROFILE:     ['active',    'locked',    'locked',    'locked',    'locked'],
  LESSON:      ['active',    'locked',    'locked',    'locked',    'locked'],
  CHECKPOINT:  ['completed', 'completed', 'locked',    'active',    'locked'],
  REMEDIATION: ['completed', 'completed', 'active',    'locked',    'locked'],
  VALIDATION:  ['completed', 'completed', 'completed', 'completed', 'active'],
};

const COLORS = {
  completed: 'var(--brand-emerald)',
  active:    'var(--brand-amber)',
  locked:    '#94a3b8',
};

function CheckGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4 4L19 7" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
  );
}
function LockGlyph() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
  );
}

export default function LessonProgressBar({ sessionPhase }) {
  const states = PHASE_NODE_STATE[sessionPhase] || PHASE_NODE_STATE.LESSON;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', padding: '12px 8px', flexShrink: 0 }}>
      {NODES.map((label, i) => {
        const state = states[i];
        const color = COLORS[state];
        const filled = state === 'completed' || state === 'active';
        return (
          <div key={label} style={{ display: 'flex', alignItems: 'center', flex: i < NODES.length - 1 ? 1 : '0 0 auto', minWidth: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flexShrink: 0 }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                background: filled ? color : 'transparent',
                border: filled ? 'none' : `1.6px solid ${color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 11, fontWeight: 800, flexShrink: 0,
              }}>
                {state === 'completed' ? <CheckGlyph /> : state === 'locked' ? <LockGlyph /> : (i + 1)}
              </div>
              <span style={{ fontSize: 11, fontWeight: state === 'active' ? 700 : 600, color, whiteSpace: 'nowrap' }}>{label}</span>
            </div>
            {i < NODES.length - 1 && (
              <div style={{ flex: 1, height: 2, margin: '0 6px', marginBottom: 18, borderRadius: 99, background: state === 'completed' ? 'var(--brand-emerald)' : '#94a3b8', minWidth: 12 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}
